import 'twin.macro';
import React, { useState } from 'react';
import SimpleNarrowPage from '@teetsh/app/src/components/Layouts/SimpleNarrowPage';
import { useFdpTemplates } from './useFdpTemplates';
import TemplateCard, { TagData } from '@teetsh/app/src/components/TemplateCard';
import { PreviewModal } from '@teetsh/app/src/containers/EmploiDuTemps/New/PreviewModal';
import ErrorFallback from '@teetsh/app/src/components/Errors/ErrorFallback';
import { useNavigate } from 'react-router-dom';
import { useCreateStandaloneFicheDePrep } from '@teetsh/app/src/queries/fdp/useCreateStandaloneFicheDePrep';
import { OnboardingModulePageWrapper } from '@teetsh/app/src/components/OnboardingModulePageWrapper';
import { useTranslation } from 'react-i18next';
import { BadgeColor } from '@teetsh/app/src/types/colors';

export function TemplatePicker() {
  const { t } = useTranslation(['common', 'sequence']);
  const { isLoading, data, error } = useFdpTemplates();
  const [previewUrl, setPreviewUrl] = useState<string | undefined>(undefined);
  const navigate = useNavigate();
  const createFdp = useCreateStandaloneFicheDePrep();

  const handleSelect = async (documentId: string) => {
    try {
      const fdp = await createFdp.mutateAsync({ templateId: documentId });
      // Navigate to the newly created FDP
      navigate(`/fiches-de-prep/${fdp.id}`);
    } catch {
      // Error is already handled by the hook's onError
    }
  };

  if (isLoading) {
    return (
      <OnboardingModulePageWrapper
        title={t('sequence:fdpNew.templatePicker.title')}
        onBack={() => navigate(-1)}
      >
        <SimpleNarrowPage width="5xl">
          <div tw="mt-16 mx-auto max-w-5xl grid items-center justify-center md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="tsh-skeleton" tw="aspect-[4/5]" />
            <div className="tsh-skeleton" tw="aspect-[4/5]" />
            <div className="tsh-skeleton" tw="aspect-[4/5]" />
          </div>
        </SimpleNarrowPage>
      </OnboardingModulePageWrapper>
    );
  }

  if (error) {
    return (
      <ErrorFallback
        error={error as any}
        subject={t('sequence:fdpNew.templatePicker.fetchError')}
      />
    );
  }

  const fdps = data || [];

  return (
    <OnboardingModulePageWrapper
      title={t('sequence:fdpNew.templatePicker.title')}
      onBack={() => navigate(-1)}
    >
      <SimpleNarrowPage width="5xl">
        <PreviewModal
          url={previewUrl}
          onClose={() => setPreviewUrl(undefined)}
        />

        <div tw="grid grid-cols-1 gap-6 mb-8 sm:grid-cols-2 lg:grid-cols-3">
          {fdps.map(fdp => {
            const tags: TagData[] = [
              ...fdp.niveaux.map(niveau => ({
                key: niveau,
                label: niveau,
                color: 'blue' as BadgeColor,
              })),
              ...(fdp.editor?.name
                ? [
                    {
                      key: 'editor',
                      label: t('common:editorTag'),
                      color: 'indigo' as BadgeColor,
                    },
                  ]
                : []),
            ];

            return (
              <TemplateCard
                key={fdp.documentId}
                item={fdp}
                tags={tags}
                onPreview={() => setPreviewUrl(fdp.preview)}
                onSelect={() => handleSelect(fdp.documentId)}
              />
            );
          })}
        </div>

        {fdps.length === 0 && (
          <div tw="text-center py-16">
            <p tw="text-neutral-moderate text-lg">
              {t('sequence:fdpNew.templatePicker.noTemplatesAvailable')}
            </p>
          </div>
        )}
      </SimpleNarrowPage>
    </OnboardingModulePageWrapper>
  );
}
